import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useDarkMode } from './DarkModeContext';
import FPReviewSection, { FP_STAGE_FOR_STATUS, fpRecordOwned } from './FPReviewSection';
import { fpColors, FP_GOLD, FP_NOTICE_ROLES, FPNotActiveNotice, FPPreviewBanner, fpStatusLabel, fpSamePersonNotes } from './FPAssessmentShared';
import { consumeOpenFpAssessment } from './postLoginNext';

// FPFIX: the roles the backend lets list and review assessments (fp_assessment.FP_REVIEW_ROLES).
export const FP_REVIEW_ROLES = ['compliance', 'coo', 'admin', 'hr'];

const API = '/api';

// FPFRONTEND: the F&P review chain as a section on the shared Validation page, which HR,
// compliance and the COO all already reach from their own shells.
//
// WORKLIST SOURCE: the review roles (FP_REVIEW_ROLES) read GET /fp-assessment/, every record.
// FPFIX2: a LINE MANAGER outside those roles reads GET /fp-assessment/team/assessments, which returns only the
// assessments of their own direct reports, and owns the manager step on exactly those. Which of a
// reviewer's rows are their own direct reports comes from the reviewer context
// (direct_report_ids), so an HR or compliance account that also manages people gets the manager
// step for its own reports too, as the backend already allowed.
//
// FPFIX: the viewer's role and whether an hr account exists come from
// GET /fp-assessment/reviewer/context, so an admin without an employee record is recognised and
// the COO owns the HR step while no hr account exists. A viewer who is neither a review role nor
// anybody's manager (a Country Lead, finance, payroll or employee with no reports) gets no F&P
// section and makes no list request.
//
// FPSWITCH: while F&P is switched off, coo, admin and compliance see the "not active yet" notice
// here. Existing records stay listed exactly as before for whoever could already see them; only
// the notice is added. Anything but a definite true from /fp-assessment/status/enabled is off.
//
// FPFIX2: statuses read as labels in the reader's language (fpStatusLabel), and completed
// assessments are listed under their own heading with a period filter and a name search.
export default function FPValidationSection() {
  const { t } = useTranslation();
  const { isDarkMode } = useDarkMode();
  const c = fpColors(isDarkMode);

  const [rows, setRows] = useState([]);
  const [me, setMe] = useState(null);
  // FPFIX: set from the reviewer context; true until the server says otherwise, as before.
  const [hrAccountExists, setHrAccountExists] = useState(true);
  const [reportIds, setReportIds] = useState([]);
  const [teamMode, setTeamMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [openFor, setOpenFor] = useState(null);
  const [fpOn, setFpOn] = useState(false);
  // FPPREVIEW: this account previews F&P while it is off for everyone else.
  const [preview, setPreview] = useState(false);
  const [periodFilter, setPeriodFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    // FPFIX: who is looking first, so a viewer outside the review roles never asks for the list.
    const ctxRes = await axios.get(`${API}/fp-assessment/reviewer/context`).catch(() => ({ data: null }));
    const ctx = (ctxRes && ctxRes.data && typeof ctxRes.data === 'object' && !Array.isArray(ctxRes.data))
      ? ctxRes.data : null;
    if (ctx && typeof ctx.hr_account_exists === 'boolean') setHrAccountExists(ctx.hr_account_exists);
    const ctxRole = ctx && ctx.role ? ctx.role : null;
    const reports = ctx && Array.isArray(ctx.direct_report_ids) ? ctx.direct_report_ids : [];
    setReportIds(reports);
    const isTeam = Boolean(ctxRole && !FP_REVIEW_ROLES.includes(ctxRole) && reports.length > 0);
    setTeamMode(isTeam);
    if (ctxRole && !FP_REVIEW_ROLES.includes(ctxRole) && !isTeam) {
      setMe({ account_role: ctxRole }); setRows([]); setLoadFailed(true); setLoading(false);
      return;
    }
    // FPSWITCH: the switch state is read alongside, and a failure reads as off.
    Promise.all([
      axios.get(isTeam ? `${API}/fp-assessment/team/assessments` : `${API}/fp-assessment/`).catch(() => null),
      axios.get(`${API}/employees/me`).catch(() => ({ data: null })),
      axios.get(`${API}/fp-assessment/status/enabled`).catch(() => ({ data: null })),
    ])
      .then(([list, meRes, st]) => {
        const meData = meRes.data || null;
        setMe(ctxRole ? { ...(meData || {}), account_role: ctxRole } : meData);
        setFpOn(Boolean(st.data && st.data.enabled === true));
        setPreview(Boolean(st.data && st.data.preview === true));
        if (list === null) { setRows([]); setLoadFailed(true); return; }
        const loaded = Array.isArray(list.data) ? list.data : [];
        setRows(loaded);
        setLoadFailed(false);
        // FPMGRMAIL: arriving from the manager's email, the assessment it names is opened.
        const wanted = consumeOpenFpAssessment();
        if (wanted && loaded.some(r => r.id === wanted)) setOpenFor(wanted);
      })
      // A viewer without list access simply gets nothing here, which is correct - it is not an
      // error state for them.
      .catch(() => { setRows([]); setLoadFailed(true); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const role = me?.account_role || me?.role || null;
  const managesRow = (r) => teamMode || reportIds.includes(r.employee_id);

  // Only the records sitting at a stage THIS viewer owns. A record awaiting someone else is not
  // listed at all, so each role sees only their own work (item 7).
  const mine = rows.filter(r => {
    const stage = FP_STAGE_FOR_STATUS[r.status];
    if (!stage) return false;
    return fpRecordOwned(r, stage, {
      role,
      isManagerOfSubject: managesRow(r),
      hrAccountExists,
    });
  });

  // FPFIX2: the completed assessments, filtered by period and by name.
  const completed = rows.filter(r => r.status === 'completed');
  const periods = Array.from(new Set(completed.map(r => String(r.period)))).sort().reverse();
  const needle = nameFilter.trim().toLowerCase();
  const completedShown = completed.filter(r =>
    (!periodFilter || String(r.period) === periodFilter)
    && (!needle || String(r.employee_name || '').toLowerCase().includes(needle)));

  // FPSWITCH: the notice shows for the notice roles while off, even with nothing listed.
  const showNotice = !fpOn && FP_NOTICE_ROLES.includes(role);

  if (loading) return null;
  if (loadFailed && !showNotice) return null;          // no list access for this viewer: render nothing
  if (mine.length === 0 && completed.length === 0 && !showNotice) return null;   // nothing for them

  const btn = {
    background: 'transparent', color: FP_GOLD, border: `1px solid ${FP_GOLD}`,
    borderRadius: '6px', padding: '6px 12px', fontSize: '12.5px',
    fontWeight: 600, cursor: 'pointer',
  };
  const input = {
    fontSize: '13px', padding: '6px 8px', borderRadius: '6px', border: c.border,
    background: 'transparent', color: c.text,
  };

  const row = (r, label) => (
    <div key={r.id} style={{ border: c.border, borderRadius: '8px', padding: '12px', marginBottom: '10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <div style={{ fontSize: '13px', color: c.text }}>
          {r.employee_name && <span style={{ fontWeight: 600 }}>{r.employee_name} · </span>}
          <span style={{ fontWeight: 600 }}>{t('fp.period', { period: r.period })}</span>
          <span style={{ color: c.muted }}> · {fpStatusLabel(t, r.status)}</span>
        </div>
        <button type="button" onClick={() => setOpenFor(openFor === r.id ? null : r.id)} style={btn}>
          {openFor === r.id ? t('fp.close') : label}
        </button>
      </div>
      {/* FPTWOROLES: one person completed two stages. */}
      {openFor !== r.id && fpSamePersonNotes(r, t).map(note => (
        <div key={note} data-testid="fp-same-person-row" style={{ fontSize: '12px', color: c.muted, marginTop: '6px' }}>
          {note}
        </div>
      ))}
      {openFor === r.id && (
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: c.rowBorder }}>
          <FPReviewSection
            employeeId={r.employee_id}
            period={r.period}
            role={role}
            isManagerOfSubject={managesRow(r)}
            hrAccountExists={hrAccountExists}
          />
        </div>
      )}
    </div>
  );

  return (
    <div style={{ marginTop: '28px' }}>
      <div style={{ fontSize: '15px', fontWeight: 700, color: c.text, marginBottom: '4px' }}>
        {t('fp.sectionTitle')}
      </div>
      <div style={{ height: '3px', width: '36px', background: FP_GOLD, borderRadius: '2px', marginBottom: '12px' }} />
      {showNotice && <FPNotActiveNotice text={t('fpSwitch.notActive')} isDarkMode={isDarkMode} />}
      {preview && fpOn && <FPPreviewBanner text={t('fpSwitch.preview')} isDarkMode={isDarkMode} />}
      {mine.length > 0 && (
        <div style={{ fontSize: '13px', color: c.muted, marginBottom: '12px' }}>
          {t('fp.awaitingCount', { count: mine.length })}
        </div>
      )}

      {mine.map(r => row(r, t('fp.review')))}

      {completed.length > 0 && (
        <div data-testid="fp-completed" style={{ marginTop: '18px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: c.text, marginBottom: '8px' }}>
            {t('fp.completedTitle')}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
            <select aria-label={t('fp.filterPeriod')} value={periodFilter}
                    onChange={e => setPeriodFilter(e.target.value)} style={input}>
              <option value="">{t('fp.allPeriods')}</option>
              {periods.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <input type="search" aria-label={t('fp.searchName')} placeholder={t('fp.searchName')}
                   value={nameFilter} onChange={e => setNameFilter(e.target.value)} style={input} />
          </div>
          {completedShown.length === 0 ? (
            <div style={{ fontSize: '13px', color: c.muted }}>{t('fp.noCompleted')}</div>
          ) : completedShown.map(r => row(r, t('fp.open')))}
        </div>
      )}
    </div>
  );
}
